import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  DownloadCloud,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Building2,
  GraduationCap,
  BookOpen,
  CheckSquare,
  Square,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  mapBranchCustomersToOptions,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { importTeacherAccounts, getTeacherAccountImportTemplateCourses } from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { getAssignableCoursesForBranch } from "../../../api/elearning/courses/employeeCourseAccess";
import { hasPermission } from "../../../utils/permissionUtils";
import { downloadTeacherImportTemplateXlsx } from "./teacherImportTemplateXlsx";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";

const COLUMN_HELP = [
  "File gồm 4 sheet: «Hướng dẫn», «Danh sách» (nhập liệu), «Mã khóa học» (tra mã — theo chi nhánh), «Mã ngân hàng VN».",
  "Dòng 1 sheet «Danh sách» là tiêu đề cột — không xoá, không đổi tên. Cột có dấu * là bắt buộc.",
  "Nhập mỗi giáo viên một dòng từ dòng 2. Dòng trống (không Họ tên + Email) sẽ bỏ qua.",
  "Giới tính / Miễn trừ hạn mức: chọn từ danh sách gợi ý trong Excel.",
  "Mã khóa học: nhiều mã cách nhau bằng dấu phẩy; tra sheet «Mã khóa học» (tải mẫu sau khi chọn chi nhánh).",
  "Chi tiết từng cột, ví dụ và lưu ý — xem sheet «Hướng dẫn» trong file mẫu.",
];

const groupCoursesBySubject = (courses = []) => {
  const map = {};
  for (const c of courses) {
    const key = c.subject_id || c.subject_name || "other";
    if (!map[key]) {
      map[key] = {
        subject_name: c.subject_name || "Khác",
        subject_sort_order: c.subject_sort_order ?? 0,
        courses: [],
      };
    }
    map[key].courses.push(c);
  }
  return Object.values(map).sort(
    (a, b) => a.subject_sort_order - b.subject_sort_order
  );
};

const getDefaultBranchIdFromUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u?.branch?.id || "";
  } catch {
    return "";
  }
};

const TeacherAccountImportModal = ({ isOpen, onClose, onRefresh }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [partnerCustomerId, setPartnerCustomerId] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [assignableCourses, setAssignableCourses] = useState([]);
  const [defaultCourseIds, setDefaultCourseIds] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const canAssignCourses = hasPermission("elearning.courses.assign", "U");
  const courseGroups = useMemo(
    () => groupCoursesBySubject(assignableCourses),
    [assignableCourses]
  );

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const b = await getAllBranches_Array();
        const list = Array.isArray(b) ? b : b?.data || b?.items || [];
        setBranches(Array.isArray(list) ? list : []);
      } catch {
        setBranches([]);
      }
      const def = getDefaultBranchIdFromUser();
      setBranchId(def);
      setPartnerCustomerId("");
      setFile(null);
      setStep(1);
      setDefaultCourseIds([]);
      setAssignableCourses([]);
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !branchId || !canAssignCourses) {
      setAssignableCourses([]);
      setDefaultCourseIds([]);
      return;
    }
    (async () => {
      setLoadingCourses(true);
      try {
        const courses = await getAssignableCoursesForBranch(branchId);
        setAssignableCourses(Array.isArray(courses) ? courses : []);
      } catch {
        setAssignableCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    })();
  }, [isOpen, branchId, canAssignCourses]);

  useEffect(() => {
    if (!isOpen || !branchId) {
      setCustomerOptions([]);
      setPartnerCustomerId("");
      return;
    }
    (async () => {
      try {
        const res = await getCustomersByBranch(branchId);
        const items = res?.items || res || [];
        const arr = Array.isArray(items) ? items : [];
        setCustomerOptions(mapBranchCustomersToOptions(arr));
      } catch {
        setCustomerOptions([]);
      }
    })();
  }, [isOpen, branchId]);

  const branchSelectOptions = branches.map((b) => ({ value: b.id, label: b.name }));

  const resetImport = () => {
    setStep(1);
    setFile(null);
    setLoading(false);
    const def = getDefaultBranchIdFromUser();
    setBranchId(def);
    setPartnerCustomerId("");
    setDefaultCourseIds([]);
  };

  const toggleDefaultCourse = (courseId) => {
    setDefaultCourseIds((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    );
  };

  const toggleAllDefaultCourses = () => {
    const visibleIds = assignableCourses.map((c) => c.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => defaultCourseIds.includes(id));
    setDefaultCourseIds(allSelected ? [] : visibleIds);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.name.endsWith(".xlsx") ||
        selectedFile.name.endsWith(".xls") ||
        selectedFile.name.endsWith(".csv"))
    ) {
      setFile(selectedFile);
    } else {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
    }
  };

  const handleDownloadTemplate = async () => {
    if (!branchId) {
      showErrorToast("Vui lòng chọn chi nhánh trước khi tải file mẫu (để có sheet Mã khóa học).");
      return;
    }
    const branchName =
      branches.find((b) => String(b.id) === String(branchId))?.name || "";
    try {
      let courses = [];
      try {
        courses = await getTeacherAccountImportTemplateCourses(branchId);
      } catch {
        courses = [];
      }
      await downloadTeacherImportTemplateXlsx({ branchName, courses });
    } catch {
      showErrorToast("Không tạo được file mẫu. Thử lại sau.");
    }
  };

  const handleUpload = async () => {
    if (!branchId || !partnerCustomerId) {
      return showErrorToast("Vui lòng chọn chi nhánh và khách hàng (trường).");
    }
    if (!file) return showErrorToast("Vui lòng chọn file danh sách");
    setLoading(true);
    try {
      const res = await importTeacherAccounts(file, {
        branch_id: branchId,
        partner_customer_id: partnerCustomerId,
        default_course_ids: canAssignCourses ? defaultCourseIds : [],
      });
      showSuccessToast(res.message || `Import thành công ${res.imported} giáo viên`);
      if (res.errors?.length > 0 && res.errors.length <= 5) {
        res.errors.forEach((e) => showErrorToast(`Dòng ${e.line}: ${e.message}`));
      } else if (res.errors?.length > 5) {
        showErrorToast(`${res.errors.length} dòng lỗi (chỉ hiển thị 5 đầu trong toast)`);
      }
      onRefresh?.();
      onClose();
      resetImport();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Lỗi khi upload dữ liệu"
      );
    } finally {
      setLoading(false);
    }
  };

  const StepLabel = ({ num, title, active }) => (
    <div className={`flex items-center gap-2 ${active ? "opacity-100" : "opacity-40"}`}>
      <span
        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
          active ? "bg-red-600 text-white" : "bg-gray-200"
        }`}
      >
        {num}
      </span>
      <span className="text-[11px] font-bold uppercase tracking-wider">{title}</span>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-[120] p-4 pointer-events-none"
          >
            <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <div>
                  <h2 className="text-2xl font-black text-gray-800 flex items-center gap-3">
                    <UploadCloud className="text-red-600" /> IMPORT GIÁO VIÊN
                  </h2>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Chọn trường (KH) theo chi nhánh, sau đó tải file danh sách
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 hover:bg-white rounded-full transition-all shadow-sm border border-transparent hover:border-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-8 py-4 bg-white border-b border-gray-50 flex items-center justify-between">
                <StepLabel num={1} title="Khách hàng" active={step === 1} />
                <ChevronRight size={14} className="text-gray-300" />
                <StepLabel num={2} title="Chọn File" active={step === 2} />
                <ChevronRight size={14} className="text-gray-300" />
                <StepLabel num={3} title="Hoàn tất" active={step === 3} />
              </div>

              <div className="p-8 overflow-y-auto max-h-[60vh] custom-scrollbar">
                {step === 1 && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase flex items-center gap-2">
                        <Building2 size={14} /> Chi nhánh
                      </label>
                      <Select
                        styles={customSelectStyles}
                        options={branchSelectOptions}
                        placeholder="Chọn chi nhánh"
                        value={branchSelectOptions.find((o) => o.value === branchId) || null}
                        onChange={(opt) => {
                          setBranchId(opt?.value || "");
                          setPartnerCustomerId("");
                          setDefaultCourseIds([]);
                        }}
                        {...modalSelectPortalProps}
                      />
                      <p className="text-[10px] text-gray-400">
                        Mặc định: chi nhánh của tài khoản đang đăng nhập (có thể đổi).
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase flex items-center gap-2">
                        <GraduationCap size={14} /> Khách hàng — trường (theo chi nhánh)
                      </label>
                      <Select
                        styles={customerInModalSelectStyles}
                        options={customerOptions}
                        isDisabled={!branchId}
                        placeholder={
                          branchId ? "Chọn trường / khách hàng" : "Chọn chi nhánh trước"
                        }
                        value={customerOptions.find((o) => o.value === partnerCustomerId) || null}
                        onChange={(opt) => setPartnerCustomerId(opt?.value || "")}
                        formatOptionLabel={formatCustomerOptionLabel}
                        filterOption={filterCustomerOption}
                        {...modalSelectPortalProps}
                      />
                    </div>

                    {canAssignCourses && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <label className="text-[11px] font-black text-gray-400 uppercase flex items-center gap-2">
                            <BookOpen size={14} /> Khóa học mặc định (tuỳ chọn)
                          </label>
                          {assignableCourses.length > 0 && (
                            <button
                              type="button"
                              onClick={toggleAllDefaultCourses}
                              className="text-[10px] font-bold text-red-600 hover:text-red-700"
                            >
                              {defaultCourseIds.length === assignableCourses.length
                                ? "Bỏ chọn tất cả"
                                : "Chọn tất cả"}
                            </button>
                          )}
                        </div>
                        <div className="rounded-2xl border border-gray-100 bg-gray-50/80 max-h-44 overflow-y-auto custom-scrollbar p-3 space-y-3">
                          {loadingCourses ? (
                            <div className="flex items-center justify-center py-6 text-gray-400">
                              <Loader2 className="animate-spin mr-2" size={16} />
                              <span className="text-xs font-medium">Đang tải khóa học...</span>
                            </div>
                          ) : !branchId ? (
                            <p className="text-[11px] text-gray-400 text-center py-4">
                              Chọn chi nhánh để xem khóa học có thể gán.
                            </p>
                          ) : courseGroups.length === 0 ? (
                            <p className="text-[11px] text-gray-400 text-center py-4">
                              Chi nhánh này chưa có khóa học để gán.
                            </p>
                          ) : (
                            courseGroups.map((group) => (
                              <div key={group.subject_name} className="space-y-1.5">
                                <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">
                                  {group.subject_name}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {group.courses.map((course) => {
                                    const active = defaultCourseIds.includes(course.id);
                                    return (
                                      <button
                                        key={course.id}
                                        type="button"
                                        onClick={() => toggleDefaultCourse(course.id)}
                                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all ${
                                          active
                                            ? "border-red-200 bg-red-50 text-red-700"
                                            : "border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:text-red-600"
                                        }`}
                                      >
                                        {active ? (
                                          <CheckSquare size={12} />
                                        ) : (
                                          <Square size={12} />
                                        )}
                                        <span>{course.name}</span>
                                        {course.code ? (
                                          <span className="text-[10px] opacity-70">({course.code})</span>
                                        ) : null}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 leading-relaxed">
                          Các khóa đã chọn sẽ gán cho <strong>mọi giáo viên</strong> trong file.
                          Cột <strong>Mã khóa học</strong> trong Excel có thể ghi đè theo từng dòng.
                        </p>
                      </div>
                    )}

                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex gap-3">
                      <AlertCircle className="text-blue-500 shrink-0" size={18} />
                      <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                        Toàn bộ dòng trong file sẽ được gắn với <strong>một</strong> khách hàng đã
                        chọn. Mỗi tài khoản được tạo kèm mật khẩu ngẫu nhiên, gán vai trò giáo viên
                        đối tác và gửi email chào mừng (giống tạo từng người).
                        {canAssignCourses
                          ? " Có thể gán khóa học ngay trong bước này hoặc qua cột Mã khóa học trong file."
                          : ""}
                      </p>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6 text-center py-4 animate-in fade-in slide-in-from-right-4 duration-500">
                    <div className="border-2 border-dashed border-gray-200 rounded-[2rem] p-10 hover:border-red-400 hover:bg-red-50/30 transition-all group relative">
                      <input
                        type="file"
                        accept=".xlsx, .xls, .csv"
                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                        onChange={handleFileChange}
                      />
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                          {file ? <CheckCircle2 size={32} /> : <FileSpreadsheet size={32} />}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">
                            {file ? file.name : "Kéo thả file vào đây hoặc click để chọn"}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Hỗ trợ .xlsx, .xls, .csv</p>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 bg-red-50/50 text-red-700 text-xs font-bold uppercase tracking-wide hover:bg-red-50 transition-all"
                    >
                      <DownloadCloud size={16} />
                      Tải file mẫu xlsx
                    </button>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-left">
                      <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">
                        Cột trong mẫu (dòng 1 = tiêu đề)
                      </p>
                      <p className="text-[11px] text-slate-600 mb-2 leading-relaxed">
                        File mẫu có sheet <strong>Hướng dẫn</strong>, <strong>Danh sách</strong> (nhập liệu),{" "}
                        <strong>Mã khóa học</strong> (tra mã theo chi nhánh đã chọn) và{" "}
                        <strong>Mã ngân hàng VN</strong>.
                      </p>
                      <ul className="text-[11px] text-slate-600 leading-relaxed space-y-1.5 list-disc pl-4">
                        {COLUMN_HELP.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
                  className="px-6 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-2xl transition-all"
                >
                  {step === 1 ? "HUỶ BỎ" : "QUAY LẠI"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (step === 1) {
                      if (!branchId || !partnerCustomerId) {
                        showErrorToast("Vui lòng chọn chi nhánh và khách hàng (trường).");
                        return;
                      }
                      setStep(2);
                    } else handleUpload();
                  }}
                  disabled={loading}
                  className="px-10 py-3 bg-red-600 text-white rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-red-100 hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : step === 1 ? (
                    "TIẾP THEO"
                  ) : (
                    "BẮT ĐẦU IMPORT"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TeacherAccountImportModal;
